import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Theme } from '../lib/tokens';
import { FONTS } from '../lib/tokens';
import { Portrait } from '../components/Portrait';
import { Img } from '../components/Img';
import { Logo, CircleBtn } from '../components/common';
import { Icon } from '../components/icons';
import { useAuth } from '../hooks/useAuth';
import { SkeletonDiscover } from '../components/Skeletons';
import { hapticLight } from '../lib/haptics';
import { ethnicityLabel } from '../lib/identity';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

interface FeedCandidate {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  photos: string[];
  age: number;
  bio: string | null;
  ethnicity?: string | null;
  nationality?: string | null;
  sparkScore: number;
  sparkLabel: 'ember' | 'flame' | 'blaze';
  compatibilityHint: string;
  sparkAnswers: Array<{ prompt: string; answer: string }>;
  taste: { activities: Record<string, number> };
}

export interface CoupleFeedCandidate {
  coupleId: string;
  user1: { name: string; age: number; avatarUrl: string | null; photos: string[] };
  user2: { name: string; age: number; avatarUrl: string | null; photos: string[] };
  coupleBio: string | null;
  sparkScore: number;
  sparkLabel: 'ember' | 'flame' | 'blaze';
  compatibilityHint: string;
  combinedActivities: string[];
  likesYou?: boolean; // they already confirmed a like on us — liking back = instant pairing
}

export interface MatchInfo {
  matchId: string;
  displayName: string;
  avatarUrl: string | null;
  photos: string[];
  age: number;
}

interface DiscoverProps {
  t: Theme;
  dark: boolean;
  onOpenProfile: (p: any) => void;
  onMatch: (m: MatchInfo) => void;
}

export function DiscoverScreen({ t, onMatch }: DiscoverProps) {
  const { getIdToken, userProfile, refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const isCouples = userProfile?.mode === 'COUPLES';

  const [index, setIndex] = useState(() => Number(sessionStorage.getItem('feedIndex') || 0));
  const [pickToast, setPickToast] = useState(false);
  const [animatingDir, setAnimatingDir] = useState<'left' | 'right' | 'up' | null>(null);

  // Reset index when mode changes
  useEffect(() => {
    setIndex(0);
    sessionStorage.setItem('feedIndex', '0');
  }, [isCouples]);

  // ── Singles feed ──────────────────────────────────────────────
  const singlesQuery = useQuery({
    queryKey: ['feed', 'singles'],
    queryFn: async () => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/matches/feed`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch feed');
      return (await res.json()).candidates as FeedCandidate[];
    },
    enabled: !isCouples,
    staleTime: 1000 * 60 * 5,
  });

  // ── Couple state (day-one signup may have COUPLES mode with no couple yet) ──
  const coupleMeQuery = useQuery({
    queryKey: ['couple-me'],
    queryFn: async () => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/couples/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to fetch couple');
      return (await res.json()).couple as any | null;
    },
    enabled: isCouples,
    staleTime: 1000 * 30,
  });

  const hasCouple = !!coupleMeQuery.data;
  const needsInvite = isCouples && !coupleMeQuery.isLoading && !hasCouple;

  const inviteQuery = useQuery({
    queryKey: ['couple-invite'],
    queryFn: async () => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/couples/invite`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to fetch invite');
      return (await res.json()).invite as { code: string; link: string; email: string | null; expiresAt: string; createdAt: string } | null;
    },
    enabled: needsInvite,
    staleTime: 1000 * 15,
  });

  // ── Couples feed ──────────────────────────────────────────────
  const couplesQuery = useQuery({
    queryKey: ['feed', 'couples'],
    queryFn: async () => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/couples/feed`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch couples feed');
      return (await res.json()).candidates as CoupleFeedCandidate[];
    },
    enabled: isCouples && hasCouple,
    staleTime: 1000 * 60 * 5,
  });

  const { data, isLoading, isFetching, isError, refetch } = isCouples ? couplesQuery : singlesQuery;

  // Reset index if feed shrank
  useEffect(() => {
    if (data && index >= data.length && data.length > 0) {
      setIndex(0);
      sessionStorage.setItem('feedIndex', '0');
    }
  }, [data]);

  // ── Singles like / skip ───────────────────────────────────────
  const likeMutation = useMutation({
    mutationFn: async ({ candidateId, candidate }: { candidateId: string; candidate: FeedCandidate }) => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/matches/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ candidateId }),
      });
      return { ...(await res.json()), candidate };
    },
    onSuccess: (result) => {
      if (result.isMatch) {
        queryClient.invalidateQueries({ queryKey: ['matches'] });
        onMatch({
          matchId: result.matchId || result.candidate.userId,
          displayName: result.candidate.displayName,
          avatarUrl: result.candidate.avatarUrl,
          photos: result.candidate.photos,
          age: result.candidate.age,
        });
      }
    },
  });

  const skipMutation = useMutation({
    mutationFn: async () => {
      const token = await getIdToken();
      await fetch(`${API_BASE}/api/matches/skip`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    },
  });

  // ── Couples like / skip ───────────────────────────────────────
  const coupleLikeMutation = useMutation({
    mutationFn: async ({ targetCoupleId }: { targetCoupleId: string }) => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/couples/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ targetCoupleId }),
      });
      return res.json();
    },
    onSuccess: (result) => {
      if (result.isMatch) {
        queryClient.invalidateQueries({ queryKey: ['matches'] });
      } else if (result.pending) {
        // Dual approval: staged for the partner to confirm.
        queryClient.invalidateQueries({ queryKey: ['couple-picks'] });
        setPickToast(true);
        setTimeout(() => setPickToast(false), 2600);
      }
    },
  });

  const coupleSkipMutation = useMutation({
    mutationFn: async () => {
      const token = await getIdToken();
      await fetch(`${API_BASE}/api/couples/skip`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    },
  });

  // ── Partner invite: create / resend / cancel (waiting-for-partner state) ──
  const createInviteMutation = useMutation({
    mutationFn: async (email?: string) => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/couples/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: email || undefined }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to create invite');
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['couple-invite'] }),
  });

  const resendInviteMutation = useMutation({
    mutationFn: async () => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/couples/invite/resend`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to resend');
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['couple-invite'] }),
  });

  const cancelInviteMutation = useMutation({
    mutationFn: async () => {
      const token = await getIdToken();
      await fetch(`${API_BASE}/api/couples/invite`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['couple-invite'] });
      queryClient.invalidateQueries({ queryKey: ['couple-me'] });
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      refreshProfile();
    },
  });

  const candidates = (data || []) as any[];
  const profile = candidates[index] || null;
  const nextProfile = candidates[index + 1] || null;

  const advance = () => {
    setIndex(i => { const n = i + 1; sessionStorage.setItem('feedIndex', String(n)); return n; });
    setAnimatingDir(null);
  };

  const action = (dir: 'left' | 'right' | 'up') => {
    if (!profile || animatingDir) return;
    hapticLight();
    setAnimatingDir(dir);
    setTimeout(() => {
      if (isCouples) {
        if (dir === 'right' || dir === 'up') {
          coupleLikeMutation.mutate({ targetCoupleId: profile.coupleId });
        } else {
          coupleSkipMutation.mutate();
        }
      } else {
        if (dir === 'right' || dir === 'up') {
          likeMutation.mutate({ candidateId: profile.userId, candidate: profile });
        } else {
          skipMutation.mutate();
        }
      }
      advance();
    }, 300);
  };


  const vw = typeof window !== 'undefined' ? window.innerWidth : 430;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 900;
  const dx = animatingDir === 'right' ? vw : animatingDir === 'left' ? -vw : 0;
  const dy = animatingDir === 'up' ? -vh : 0;
  const opacity = animatingDir ? 0 : 1;

  // Fresh couples-mode signup with no couple formed yet — waiting on the
  // partner invite rather than a real feed. Checked before the feed even
  // has a chance to render (avoids a flash of "No couples nearby yet").
  if (isCouples && coupleMeQuery.isLoading) {
    return (
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: t.paper }}>
        <div style={{ padding: '6px 20px 12px', flexShrink: 0 }}><Logo t={t} size={0.85} /></div>
        <div style={{ flex: 1, position: 'relative' }}><SkeletonDiscover t={t} /></div>
      </div>
    );
  }

  if (needsInvite) {
    return (
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: t.paper }}>
        <div style={{ padding: '6px 20px 12px', flexShrink: 0 }}><Logo t={t} size={0.85} /></div>
        <WaitingForPartnerCard
          t={t}
          invite={inviteQuery.data ?? null}
          onCreateInvite={(email) => createInviteMutation.mutate(email)}
          onResend={() => resendInviteMutation.mutate()}
          onCancel={() => cancelInviteMutation.mutate()}
          creating={createInviteMutation.isPending}
          resending={resendInviteMutation.isPending}
          cancelling={cancelInviteMutation.isPending}
        />
      </div>
    );
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: t.paper }}>
      <div style={{ padding: '6px 20px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Logo t={t} size={0.85} />
        <button
          className="pm-press-soft"
          onClick={() => { setIndex(0); sessionStorage.setItem('feedIndex', '0'); refetch(); }}
          disabled={isFetching}
          style={{
            all: 'unset', cursor: isFetching ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', borderRadius: 20,
            background: t.card, border: `1px solid ${t.line}`,
            fontFamily: FONTS.sans, fontSize: 12, fontWeight: 500,
            color: isFetching ? t.inkMute : t.ink,
          }}
        >
          <span style={{ display: 'inline-block', animation: isFetching ? 'spin 0.8s linear infinite' : 'none', lineHeight: 1 }}>↺</span>
          Refresh
        </button>
      </div>

      <div style={{ padding: '0 20px 14px', display: 'flex', alignItems: 'center', gap: 8, fontFamily: FONTS.sans, fontSize: 12.5, color: t.inkSoft, flexShrink: 0 }}>
        {isCouples
          ? <>{Icon.heart({ s: 14, c: t.gold })}<span style={{ color: t.gold, fontWeight: 500 }}>Discovering couples near you</span></>
          : <>{Icon.pin({ s: 14, c: t.terracotta })}<span>Discovering people near you</span></>
        }
        {isFetching && !isLoading && (
          <div style={{ marginLeft: 'auto', width: 12, height: 12, borderRadius: '50%', border: `1.5px solid ${isCouples ? t.gold : t.terracotta}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {isLoading && (
          <div style={{ position: 'absolute', inset: 0 }}>
            <SkeletonDiscover t={t} />
          </div>
        )}

        {isError && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <div style={{ fontFamily: FONTS.sans, fontSize: 14, color: t.inkMute }}>Couldn't load profiles.</div>
          </div>
        )}

        {!isLoading && !isError && (
          <>
            {nextProfile && (
              isCouples
                ? <CoupleProfileView couple={nextProfile} t={t} style={{ position: 'absolute', inset: 0, opacity: animatingDir ? 1 : 0.6, transform: animatingDir ? 'scale(1)' : 'scale(0.96)', transition: 'all 300ms ease-out' }} />
                : <VerticalProfileView profile={nextProfile} t={t} style={{ position: 'absolute', inset: 0, opacity: animatingDir ? 1 : 0.6, transform: animatingDir ? 'scale(1)' : 'scale(0.96)', transition: 'all 300ms ease-out' }} />
            )}

            {profile && (
              isCouples
                ? <CoupleProfileView couple={profile} t={t} style={{ position: 'absolute', inset: 0, transform: `translate(${dx}px, ${dy}px)`, opacity, transition: animatingDir ? 'transform 300ms cubic-bezier(0.4,0,0.2,1), opacity 300ms' : 'none', zIndex: 10 }} />
                : <VerticalProfileView profile={profile} t={t} style={{ position: 'absolute', inset: 0, transform: `translate(${dx}px, ${dy}px)`, opacity, transition: animatingDir ? 'transform 300ms cubic-bezier(0.4,0,0.2,1), opacity 300ms' : 'none', zIndex: 10 }} />
            )}

            {!profile && !animatingDir && (
              <div style={{
                position: 'absolute', inset: 0, margin: '0 0 12px',
                borderRadius: 24, border: `1.5px dashed ${t.line}`,
                background: t.card,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
              }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: t.paperAlt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {isCouples ? Icon.heart({ s: 24, c: t.gold }) : Icon.sparkle({ s: 24, c: t.terracotta })}
                </div>
                <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 24, color: t.ink, textAlign: 'center' }}>
                  {isCouples ? 'No couples nearby yet.' : 'That\'s everyone for now.'}
                </div>
                <div style={{ fontFamily: FONTS.sans, fontSize: 13, color: t.inkMute, textAlign: 'center', maxWidth: 220, lineHeight: 1.5 }}>
                  {isCouples
                    ? 'More couples are joining —\ncheck back soon.'
                    : 'Check back tomorrow —\nLondon\'s full of people.'}
                </div>
              </div>
            )}
          </>
        )}

        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 20,
          padding: '16px 28px 24px',
          background: `linear-gradient(to top, ${t.paper} 60%, transparent)`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14,
          pointerEvents: 'none',
        }}>
          {pickToast && (
            <div style={{
              position: 'absolute', bottom: '110%', left: '50%', transform: 'translateX(-50%)',
              background: t.gold, color: t.paper, borderRadius: 100,
              padding: '8px 16px', whiteSpace: 'nowrap',
              fontFamily: FONTS.sans, fontSize: 12, fontWeight: 500,
              pointerEvents: 'none', zIndex: 100,
            }}>♥♥ Saved to your picks — your partner decides too</div>
          )}
          <CircleBtn testId="discover-skip" t={t} size={72} accent={t.terracotta} onClick={() => action('left')} style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.06)', pointerEvents: 'auto' }}>
            {Icon.x({ s: 30, c: t.terracotta, w: 2.2 })}
          </CircleBtn>
          <CircleBtn testId="discover-like" t={t} size={72} accent={t.sage} onClick={() => action('right')} style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.06)', pointerEvents: 'auto' }}>
            {Icon.heart({ s: 30, c: t.sage, fill: t.sage, w: 0 })}
          </CircleBtn>
        </div>
      </div>
    </div>
  );
}

// ─── Waiting for partner (couples day-one signup, no couple yet) ──────────────

function pillBtnStyle(t: Theme): React.CSSProperties {
  return {
    all: 'unset', cursor: 'pointer', padding: '9px 16px', borderRadius: 100,
    background: t.paperAlt, border: `1px solid ${t.line}`,
    fontFamily: FONTS.sans, fontSize: 12.5, fontWeight: 500, color: t.ink,
  };
}

function WaitingForPartnerCard({
  t, invite, onCreateInvite, onResend, onCancel, creating, resending, cancelling,
}: {
  t: Theme;
  invite: { code: string; link: string; email: string | null } | null;
  onCreateInvite: (email?: string) => void;
  onResend: () => void;
  onCancel: () => void;
  creating: boolean; resending: boolean; cancelling: boolean;
}) {
  const [copyToast, setCopyToast] = useState<'code' | 'link' | null>(null);
  const [resendLocked, setResendLocked] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [emailDraft, setEmailDraft] = useState('');

  const copy = async (value: string, which: 'code' | 'link') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyToast(which);
      setTimeout(() => setCopyToast(null), 1800);
    } catch { /* clipboard may be unavailable — silently ignore */ }
  };

  const handleResend = () => {
    if (resendLocked) return;
    onResend();
    setResendLocked(true);
    setTimeout(() => setResendLocked(false), 30_000);
  };

  if (!invite) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 28px', textAlign: 'center', gap: 16 }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: `${t.gold}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 24 }}>♥♥</span>
        </div>
        <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 26, color: t.ink }}>Invite your partner</div>
        <div style={{ fontFamily: FONTS.sans, fontSize: 13.5, color: t.inkMute, lineHeight: 1.5, maxWidth: 280 }}>
          Set them up with a link and code — they finish their own profile and you're paired.
        </div>
        <input
          type="email"
          value={emailDraft}
          onChange={e => setEmailDraft(e.target.value)}
          placeholder="their@email.com (optional)"
          style={{
            width: '100%', maxWidth: 280, boxSizing: 'border-box',
            padding: '13px 16px', borderRadius: 14,
            background: t.card, border: `1px solid ${t.line}`,
            fontFamily: FONTS.sans, fontSize: 14, color: t.ink, outline: 'none',
          }}
        />
        <button
          onClick={() => onCreateInvite(emailDraft.trim() || undefined)}
          disabled={creating}
          className="pm-press-soft"
          style={{
            all: 'unset', cursor: creating ? 'default' : 'pointer',
            padding: '13px 28px', borderRadius: 100,
            background: t.gold, color: '#fff',
            fontFamily: FONTS.sans, fontSize: 14, fontWeight: 600,
            opacity: creating ? 0.6 : 1,
          }}
        >
          {creating ? 'Sending…' : (emailDraft.trim() ? 'Send invite' : 'Get a link')}
        </button>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 28px', textAlign: 'center', gap: 18, position: 'relative' }}>
      {copyToast && (
        <div style={{
          position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)',
          background: t.ink, color: t.paper, borderRadius: 100, padding: '8px 16px',
          fontFamily: FONTS.sans, fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap',
        }}>
          {copyToast === 'code' ? 'Code copied ✓' : 'Link copied ✓'}
        </div>
      )}

      <div style={{ width: 56, height: 56, borderRadius: '50%', background: `${t.gold}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${t.gold}`, borderTopColor: 'transparent', animation: 'spin 1.2s linear infinite' }} />
      </div>

      <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 26, color: t.ink }}>Waiting for your partner</div>

      {invite.email && (
        <div style={{ fontFamily: FONTS.sans, fontSize: 13.5, color: t.inkSoft, lineHeight: 1.5 }}>
          We sent an invite to <strong style={{ color: t.ink }}>{invite.email}</strong>.
        </div>
      )}

      <div
        onClick={() => copy(invite.code, 'code')}
        style={{
          cursor: 'pointer', padding: '16px 24px', borderRadius: 16,
          background: t.card, border: `1px solid ${t.line}`,
          fontFamily: FONTS.mono, fontSize: 22, fontWeight: 700, letterSpacing: 4, color: t.ink,
        }}
      >
        {invite.code}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button onClick={() => copy(invite.code, 'code')} className="pm-press-soft" style={pillBtnStyle(t)}>Copy code</button>
        <button onClick={() => copy(invite.link, 'link')} className="pm-press-soft" style={pillBtnStyle(t)}>Copy invite link</button>
        {invite.email && (
          <button
            onClick={handleResend}
            disabled={resendLocked || resending}
            className="pm-press-soft"
            style={{ ...pillBtnStyle(t), opacity: resendLocked || resending ? 0.5 : 1 }}
          >
            {resendLocked ? 'Sent ✓' : resending ? 'Sending…' : 'Resend email'}
          </button>
        )}
      </div>

      {!confirmCancel ? (
        <button
          onClick={() => setConfirmCancel(true)}
          style={{ all: 'unset', cursor: 'pointer', marginTop: 10, fontFamily: FONTS.sans, fontSize: 12.5, color: t.inkMute, textDecoration: 'underline' }}
        >
          Cancel invite and continue solo
        </button>
      ) : (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <div style={{ fontFamily: FONTS.sans, fontSize: 12.5, color: t.inkMute }}>This switches you back to solo mode — sure?</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setConfirmCancel(false)} style={pillBtnStyle(t)}>Never mind</button>
            <button
              onClick={onCancel}
              disabled={cancelling}
              style={{ ...pillBtnStyle(t), background: t.terracotta, color: '#fff', border: 'none' }}
            >
              {cancelling ? 'Switching…' : 'Yes, go solo'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Singles Profile View ─────────────────────────────────────────────────────

function VerticalProfileView({ profile: p, t, style = {} }: { profile: FeedCandidate; t: Theme; style?: React.CSSProperties }) {
  const photos = p.photos || [];
  const activities = p.taste?.activities
    ? Object.keys(p.taste.activities).sort((a, b) => p.taste.activities[b] - p.taste.activities[a]).slice(0, 5)
    : [];
  const prompts = p.sparkAnswers || [];
  const identityChips = [ethnicityLabel(p.ethnicity), p.nationality].filter((v): v is string => !!v);

  return (
    <div style={{ overflowY: 'auto', background: t.paper, ...style }} className="scroll-container">
      <div style={{ position: 'relative', height: 480, margin: '0 12px', borderRadius: 20, overflow: 'hidden' }}>
        {photos[0]
          ? <Img placeholder={t.paperAlt} src={photos[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} draggable={false} />
          : <Portrait seed={p.userId} style={{ position: 'absolute', inset: 0 }} />
        }
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '80px 22px 22px', background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <h2 style={{ fontFamily: FONTS.display, fontWeight: 400, fontStyle: 'italic', fontSize: 36, lineHeight: 1, color: '#fff', margin: 0, letterSpacing: -0.5 }}>{p.displayName},</h2>
            <span style={{ fontFamily: FONTS.sans, fontSize: 24, fontWeight: 300, color: 'rgba(255,255,255,0.9)' }}>{p.age}</span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <span style={{ padding: '5px 12px', borderRadius: 100, background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', fontFamily: FONTS.sans, fontSize: 12, fontWeight: 500 }}>{p.sparkLabel}</span>
            <span style={{ padding: '5px 12px', borderRadius: 100, background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', fontFamily: FONTS.sans, fontSize: 12, fontWeight: 500 }}>{p.sparkScore}% fit</span>
          </div>
        </div>
      </div>

      {p.bio && (
        <div style={{ padding: '24px 24px 12px' }}>
          <div style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, color: t.inkMute, marginBottom: 6 }}>About me</div>
          <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 20, color: t.ink, lineHeight: 1.35 }}>"{p.bio}"</div>
        </div>
      )}

      {identityChips.length > 0 && (
        <div data-clarity-mask="true" style={{ padding: `${p.bio ? 0 : 20}px 24px 8px` }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {identityChips.map((chip, i) => (
              <span key={i} style={{ padding: '5px 11px', borderRadius: 100, border: `1px solid ${t.line}`, background: t.paperAlt, fontFamily: FONTS.sans, fontSize: 12, color: t.inkSoft }}>{chip}</span>
            ))}
          </div>
        </div>
      )}

      {activities.length > 0 && (
        <div style={{ padding: '8px 24px 20px' }}>
          <div style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, color: t.inkMute, marginBottom: 10 }}>Into</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {activities.map((act, i) => (
              <span key={i} style={{ padding: '6px 12px', borderRadius: 100, border: `1px solid ${t.line}`, background: t.card, fontFamily: FONTS.sans, fontSize: 12.5, color: t.ink }}>{act}</span>
            ))}
          </div>
        </div>
      )}

      {photos[1] && <div style={{ padding: '12px' }}><Img placeholder={t.paperAlt} src={photos[1]} alt="" style={{ width: '100%', height: 420, objectFit: 'cover', borderRadius: 20 }} draggable={false} /></div>}

      {prompts[0] && (
        <div style={{ padding: '12px' }}>
          <div style={{ padding: '24px 20px', borderRadius: 20, background: t.card, border: `1px solid ${t.line}` }}>
            <div style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: t.inkMute, marginBottom: 8 }}>{prompts[0].prompt}</div>
            <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 24, color: t.ink, lineHeight: 1.3 }}>"{prompts[0].answer}"</div>
          </div>
        </div>
      )}

      {p.compatibilityHint && (
        <div style={{ padding: '12px' }}>
          <div style={{ padding: '24px 20px', borderRadius: 20, background: t.paperAlt, border: `1px solid ${t.line}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              {Icon.sparkle({ s: 15, c: t.terracotta })}
              <span style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: t.terracotta, fontWeight: 600 }}>Why you might fit</span>
            </div>
            <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 19, color: t.ink, lineHeight: 1.3 }}>{p.compatibilityHint}</div>
          </div>
        </div>
      )}

      {prompts[1] && (
        <div style={{ padding: '12px' }}>
          <div style={{ padding: '24px 20px', borderRadius: 20, background: t.blush, border: `1px solid ${t.blush}` }}>
            <div style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: t.terracotta, opacity: 0.8, marginBottom: 8 }}>{prompts[1].prompt}</div>
            <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 24, color: t.ink, lineHeight: 1.3 }}>"{prompts[1].answer}"</div>
          </div>
        </div>
      )}

      {photos.slice(2).map((photo, i) => (
        <div key={i} style={{ padding: '12px' }}><Img placeholder={t.paperAlt} src={photo} alt="" style={{ width: '100%', height: 420, objectFit: 'cover', borderRadius: 20 }} draggable={false} /></div>
      ))}
      <div style={{ height: 160 }} />
    </div>
  );
}

// ─── Couples Profile View ─────────────────────────────────────────────────────
// Exported: also used by MatchesScreen's Partner's Picks detail overlay.

export function CoupleProfileView({ couple: c, t, style = {} }: { couple: CoupleFeedCandidate; t: Theme; style?: React.CSSProperties }) {
  const photo1 = c.user1.photos?.[0] || c.user1.avatarUrl;
  const photo2 = c.user2.photos?.[0] || c.user2.avatarUrl;

  return (
    <div style={{ overflowY: 'auto', background: t.paper, ...style }} className="scroll-container">

      {/* Dual photo hero */}
      <div style={{ margin: '0 12px', borderRadius: 20, overflow: 'hidden', height: 480, position: 'relative', display: 'flex' }}>
        {/* Left — user1 */}
        <div style={{ flex: 1, position: 'relative' }}>
          {photo1
            ? <Img placeholder={t.paperAlt} src={photo1} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} draggable={false} />
            : <Portrait seed={c.user1.name} style={{ position: 'absolute', inset: 0 }} />
          }
        </div>

        {/* Divider */}
        <div style={{ width: 3, background: t.paper, zIndex: 2, flexShrink: 0 }} />

        {/* Right — user2 */}
        <div style={{ flex: 1, position: 'relative' }}>
          {photo2
            ? <Img placeholder={t.paperAlt} src={photo2} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} draggable={false} />
            : <Portrait seed={c.user2.name} style={{ position: 'absolute', inset: 0 }} />
          }
        </div>

        {/* Gradient overlay with names */}
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 3,
          padding: '80px 20px 20px',
          background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.35) 55%, transparent 100%)',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <h2 style={{ fontFamily: FONTS.display, fontWeight: 400, fontStyle: 'italic', fontSize: 30, lineHeight: 1.1, color: '#fff', margin: 0, letterSpacing: -0.5 }}>
            {c.user1.name} & {c.user2.name}
          </h2>
          <div style={{ fontFamily: FONTS.sans, fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>
            {c.user1.age} & {c.user2.age}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {c.likesYou && (
              <span style={{ padding: '5px 12px', borderRadius: 100, background: t.gold, border: '1px solid rgba(255,255,255,0.35)', color: '#fff', fontFamily: FONTS.sans, fontSize: 12, fontWeight: 700 }}>♥♥ They like you two</span>
            )}
            <span style={{ padding: '5px 12px', borderRadius: 100, background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', fontFamily: FONTS.sans, fontSize: 12, fontWeight: 500 }}>{c.sparkLabel}</span>
            <span style={{ padding: '5px 12px', borderRadius: 100, background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', fontFamily: FONTS.sans, fontSize: 12, fontWeight: 500 }}>♥♥ {c.sparkScore}% fit</span>
          </div>
        </div>
      </div>

      {/* Couple bio */}
      {c.coupleBio && (
        <div style={{ padding: '24px 24px 12px' }}>
          <div style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, color: t.gold, marginBottom: 8, fontWeight: 600 }}>About them</div>
          <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 20, color: t.ink, lineHeight: 1.4 }}>"{c.coupleBio}"</div>
        </div>
      )}

      {/* Combined activities */}
      {c.combinedActivities.length > 0 && (
        <div style={{ padding: '8px 24px 20px' }}>
          <div style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, color: t.inkMute, marginBottom: 10 }}>They're into</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {c.combinedActivities.map((act, i) => (
              <span key={i} style={{ padding: '6px 12px', borderRadius: 100, border: `1px solid ${t.gold}55`, background: `${t.gold}10`, fontFamily: FONTS.sans, fontSize: 12.5, color: t.ink }}>{act}</span>
            ))}
          </div>
        </div>
      )}

      {/* Compatibility hint */}
      {c.compatibilityHint && (
        <div style={{ padding: '4px 12px 12px' }}>
          <div style={{ padding: '20px', borderRadius: 20, background: `${t.gold}12`, border: `1px solid ${t.gold}40` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <span style={{ fontSize: 13 }}>♥♥</span>
              <span style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: t.gold, fontWeight: 600 }}>Why you'd click</span>
            </div>
            <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 19, color: t.ink, lineHeight: 1.3 }}>{c.compatibilityHint}</div>
          </div>
        </div>
      )}

      {/* Individual photos row */}
      {(c.user1.photos?.[1] || c.user2.photos?.[1]) && (
        <div style={{ padding: '4px 12px 12px', display: 'flex', gap: 8 }}>
          {c.user1.photos?.[1] && (
            <div style={{ flex: 1, borderRadius: 16, overflow: 'hidden', height: 220 }}>
              <Img placeholder={t.paperAlt} src={c.user1.photos[1]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} draggable={false} />
            </div>
          )}
          {c.user2.photos?.[1] && (
            <div style={{ flex: 1, borderRadius: 16, overflow: 'hidden', height: 220 }}>
              <Img placeholder={t.paperAlt} src={c.user2.photos[1]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} draggable={false} />
            </div>
          )}
        </div>
      )}

      <div style={{ height: 160 }} />
    </div>
  );
}
